import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
    onClickCreate,
    onClickDelete,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import { createAppPageHeaderTop, createManageExportCsv, createManageSelect, createPage, createRowActionOpenUrl } from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { validateError } from "../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./storage-location.condition";
import { createColumns, createField } from "./storage-location.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/storageLocation";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
//export const callReportData = ({ data }) => store.dispatch(`${actionPrefix}/reportData`, { data });

export const onSearch = ({ props, page, state }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { isDistinct: true, ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, state, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs, state });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { isLocation, menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo.refParent?.id || userInfo.id }]);
        whereGroup.push([
            { name: "refUserCompany.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "2" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "1" },
            {
                name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorize.id",
                type: "Getpath",
                operator: "=",
                value: "refUserCompany.id"
            },
            { openBucket: true, name: "refUser.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },
            { logic: "OR", closeBucket: true, name: "refUser.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }
        ]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ constant: true, name: "1", type: "String", operator: "=", value: "1" }]);
    }
    return [
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "isLocation", type: "Boolean", operator: "=", value: isLocation },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        refDepartment: null,
        isLocation: null,
        name: null,
        idTax: null,
        email: null,
        tel: null,
        fax: null,
        refOwnershipType: null,
        productionArea: null,
        horsepower: null,
        worker: null,
        isSuitablePosition: null,
        isRoyalPalace: null,
        isHome: null,
        isShoppingArea: null,
        isPublicPlace: null,
        isIndustrialArea: null,
        isSafety: null,
        isWaste: null,
        isChemical: null,
        approveCode: null,
        refStatus: null,
        isTemp: null,
        addresses: [],
        subs: [],
        experts: [],
        types: [],
        productTypes: [],
        attachments: []
    };
};

export const createRules = () => {
    return {};
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.name} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = () => {
    const promises = [];
    return Promise.all(promises);
};

export const initState = ({ props }) => {
    const { menuContext, isLocation } = props;
    if (stateGroup[isLocation]) {
        return stateGroup[isLocation];
    }
    const state = {
        columns: createColumns({ props }),
        page: createPage({
            options: {
                select: ["*", "refUserCompany.*", "refUserCompany.refPersonType.*", "refStatus.*"],
                children: [
                    {
                        name: "addresses",
                        select: ["refProvince.*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    }
                ],
                sort: [{ name: "updateDate", type: "DESC" }],
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction()
    };
    state.manages = [
        createManageSelect({ menuContext }),
        /*
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading })
        }),
        */
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [
        /* createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) }) */
    ];
    state.rowActions = [
        /*
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
        */
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callReport: ({ record }) => callReportData({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/dof/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
    return (stateGroup[isLocation] = state);
};

export const initForm = ({ props, data, refs, state }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, state })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs, state }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const stateGroup = [];
